import React, { useEffect, useState } from "react";

import { Card, CardBody } from "@nextui-org/react";
import { BookingData, BookingStatus } from "../../types/booking";
import { useStore } from "../../app/stores/store";
import { toast } from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import LoadingSpinner from "../common/LoadingSpinner";
import { observer } from "mobx-react-lite";
import RefundRequestModal from "./RefundRequestModal";

const BookingListCustomer = () => {
  const { bookingStore, userStore } = useStore();
  const [bookings, setBookings] = useState<BookingData[]>([]);
  const [loading, setLoading] = useState(false);

  const [selectedBooking, setSelectedBooking] = useState<BookingData | null>(
    null
  );
  const [refundSuccess, setRefundSuccess] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    // Function to load bookings
    const loadBookings = async () => {
      setLoading(true);
      try {
        const bookingsById = await bookingStore.getBookingByCustomerId(
          userStore.user!.id!
        );
        const bookingsByEmail = await bookingStore.getBookingByEmail(
          userStore.user!.email
        );

        const merged = [...(bookingsById || []), ...(bookingsByEmail || [])];
        const uniqueBookings = Array.from(
          new Map(merged.map((b) => [b.id, b])).values()
        );

        setBookings(uniqueBookings);
      } catch (error) {
        toast.error("Failed to load bookings.");
      } finally {
        setLoading(false);
      }
    };

    // On mount, load bookings once
    loadBookings();

    // Reload bookings if refundSuccess flag is true
    if (refundSuccess) {
      loadBookings().then(() => setRefundSuccess(false));
    }
  }, [refundSuccess, userStore.user?.id, userStore.user?.email]);

  const handleRefundClick = (booking: BookingData) => {
    setSelectedBooking(booking);
  };

  if (loading) return <LoadingSpinner />;

  if (bookings.length === 0) {
    return (
      <div className="text-center py-10 text-gray-500 font-medium">
        No bookings found.
      </div>
    );
  }
  const renderStatusLabel = (status: number) => {
    switch (status) {
      case 0:
        return <span className="status-pending">{BookingStatus.Pending}</span>;
      case 1:
        return (
          <span className="status-inprocess">{BookingStatus.InProcess}</span>
        );
      case 2:
        return (
          <span className="status-canceled">{BookingStatus.Canceled}</span>
        );
      case 3:
        return (
          <span className="status-completed">{BookingStatus.Completed}</span>
        );
      default:
        return <span>{status}</span>;
    }
  };

  return (
    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3 px-4 py-6 custom-container">
      {bookings.map((booking) => (
        <Card key={booking.id} className="shadow-md border border-gray-100">
          <CardBody>
            <div className="space-y-2">
              <h2 className="text-lg font-semibold text-primary">
                {booking.serviceName}
              </h2>
              <p className="text-sm text-gray-500">
                Booking Code:{" "}
                <span className="font-medium text-black">
                  {booking.bookingCode}
                </span>
              </p>
              <p className="text-sm text-gray-500">
                Customer:{" "}
                <span className="font-medium text-black">
                  {booking.customerName}
                </span>
              </p>
              <p className="text-sm text-gray-500">
                Date:{" "}
                <span className="font-medium text-black">
                  {booking.bookingDate ?? "N/A"}
                </span>
              </p>
              <p className="text-sm text-gray-500">
                Status:{" "}
                <span className="px-2 py-0.5 rounded text-white text-xs bg-blue-500">
                  {renderStatusLabel(booking.bookingStatus!)}
                </span>
              </p>
              {/* {booking.clients && booking.clients.length > 0 && (
                <p>Client name: {booking.clients[0]?.name}</p>
              )} */}

              {booking.paymentStatus === "Completed" && (
                <button
                  className="bg-slate-700 hover:bg-slate-900 text-white px-4 py-1 rounded"
                  onClick={() => handleRefundClick(booking)}
                >
                  Request Refund
                </button>
              )}
            </div>
          </CardBody>
        </Card>
      ))}

      {/* Refund Modal */}
      <RefundRequestModal
        key={selectedBooking?.id ?? "none"}
        booking={selectedBooking}
        onClose={() => setSelectedBooking(null)}
        open={!!selectedBooking}
        onRefundSuccess={() => setRefundSuccess(true)}
      />
    </div>
  );
};

export default observer(BookingListCustomer);